/**
 * Bomb target FX — the artillery-style telegraph for the bombs (family `bombs`).
 * There is NO projectile body (owner spec): the SPINNING lock-on reticle itself
 * FLIES INTO PLACE and the {@link Explosion} fires when it arrives. *Core decides,
 * Phaser renders*: core flies the hazard centre `(x,y)` from off-screen to the
 * landing point `to` over the telegraph (holding at `to`), then detonates at active.
 * This paints the reticle ON that live centre, so it:
 *
 *   - sweeps in from off-screen, spinning + growing toward the blast footprint and
 *     reddening + spinning faster as it nears the target, locking on as it settles, then
 *   - vanishes at detonation (the {@link Explosion} blast takes over).
 *
 * Decorative + pooled, edge-consistent for replays.
 */
import Phaser from 'phaser';
import type { CircleMoverHazard } from '../core';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

const TARGET_TEX = 64;
const RETICLE_DEPTH = 5; // at the hazard layer — there's no bomb body to sit under

export class BombFx {
  private readonly scene: Phaser.Scene;
  private readonly free: Phaser.GameObjects.Image[] = [];
  private readonly all: Phaser.GameObjects.Image[] = [];
  private readonly state = new Map<number, Phaser.GameObjects.Image>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Paint the flying-in target reticle for one bomb at the current world time. */
  update(h: CircleMoverHazard, elapsedMs: number): void {
    const fireAt = h.bornMs + h.telegraphMs;
    let reticle = this.state.get(h.id);
    if (!reticle) {
      reticle = this.acquire();
      this.state.set(h.id, reticle);
    }

    if (elapsedMs < fireAt) {
      const p = Phaser.Math.Clamp((elapsedMs - h.bornMs) / Math.max(1, h.telegraphMs), 0, 1);
      const pulse = 0.5 + 0.5 * Math.sin(elapsedMs / 70);
      // Grow toward (but a little under) the blast footprint as it nears the target.
      const dia = h.r * 2 * (0.55 + 0.5 * p);
      // Ride the LIVE hazard centre `(x,y)` so the reticle flies in from off-screen
      // and locks onto the landing point `to` as it settles.
      reticle
        .setPosition(h.x, h.y)
        .setVisible(true)
        .setScale(dia / TARGET_TEX)
        .setRotation(elapsedMs / 220 + p * 5) // spins, accelerating toward impact
        .setTint(p < 0.6 ? HAZARD_COLORS.targetRing : HAZARD_COLORS.targetDot) // gold → red
        .setAlpha((0.55 + 0.4 * pulse) * (0.4 + 0.6 * p));
    } else {
      reticle.setVisible(false); // detonation: the blast FX takes over
    }
  }

  /** Release reticles whose bomb id is no longer live. */
  sweep(alive: Set<number>): void {
    for (const [id, r] of this.state) {
      if (alive.has(id)) continue;
      this.release(r);
      this.state.delete(id);
    }
  }

  reset(): void {
    for (const r of this.state.values()) this.release(r);
    this.state.clear();
  }

  destroy(): void {
    this.state.clear();
    for (const r of this.all) r.destroy();
    this.all.length = 0;
    this.free.length = 0;
  }

  private acquire(): Phaser.GameObjects.Image {
    const r = this.free.pop() ?? this.make();
    r.setActive(true).setVisible(true);
    return r;
  }
  private release(r: Phaser.GameObjects.Image): void {
    r.setActive(false).setVisible(false).clearTint().setScale(1).setAlpha(1).setAngle(0);
    this.free.push(r);
  }
  private make(): Phaser.GameObjects.Image {
    const r = this.scene.add.image(0, 0, HAZARD_TEX.target).setDepth(RETICLE_DEPTH);
    this.all.push(r);
    return r;
  }
}
